import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, NumberInput, Pagination, Progress, Select, Switch, Table, Text, TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconDeviceFloppy, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useClasses } from '../hooks/useLookups';
import { formatSom } from '../lib/format';

/** GET /payments/plans qatori — faol o'quvchi va uning oylik summasi (yo'q bo'lsa null). */
interface PlanRow {
  id: string;
  fullName: string;
  classId: string | null;
  className: string | null;
  monthlyAmount: number | null;
  discount: number;
  note: string | null;
}
interface PlansResp {
  items: PlanRow[];
  total: number;
  page: number;
  pages: number;
  activeTotal: number;
  withPlan: number;
}
/** Foydalanuvchi o'zgartirgan, hali saqlanmagan qiymatlar. */
interface Tahrir { monthlyAmount: number | ''; discount: number; note: string }

const LIMIT = 50;

/**
 * OYLIK TO'LOVLAR — faol o'quvchilarning oylik summasi va chegirmasini bitta jadvaldan
 * kiritish. Qarzdorlik shu summadan hisoblanadi (`lib/balance.ts`): summasi yo'q
 * o'quvchi hech qachon qarzdor bo'lmaydi.
 *
 * O'zgarishlar sahifa almashganda ham saqlanib turadi va "Saqlash" ularni BITTA
 * so'rovda yozadi (server: hammasi yoki hech biri).
 */
export function TuitionPlansPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: classes } = useClasses();

  const [classF, setClassF] = useState('');
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [missing, setMissing] = useState(false);
  const [page, setPage] = useState(1);
  const [tahrirlar, setTahrirlar] = useState<Record<string, Tahrir>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['tuition-plans', classF, qDeb, missing, page],
    queryFn: async () =>
      (await api.get<PlansResp>('/payments/plans', {
        params: { classId: classF || undefined, q: qDeb || undefined, missing: missing ? 1 : undefined, page, limit: LIMIT },
      })).data,
    placeholderData: (old) => old,
  });

  const qiymat = (r: PlanRow): Tahrir =>
    tahrirlar[r.id] ?? { monthlyAmount: r.monthlyAmount ?? '', discount: r.discount, note: r.note ?? '' };

  /** Qiymat serverdagi bilan bir xil bo'lib qolsa — "o'zgargan" ro'yxatidan chiqadi. */
  const ozgartir = (r: PlanRow, patch: Partial<Tahrir>) => {
    const yangi = { ...qiymat(r), ...patch };
    const asl = { monthlyAmount: r.monthlyAmount ?? '', discount: r.discount, note: r.note ?? '' };
    setTahrirlar((old) => {
      const n = { ...old };
      if (yangi.monthlyAmount === asl.monthlyAmount && yangi.discount === asl.discount && yangi.note === asl.note) delete n[r.id];
      else n[r.id] = yangi;
      return n;
    });
  };

  const ozgarganlar = Object.entries(tahrirlar);
  const summasiz = ozgarganlar.filter(([, v]) => !v.monthlyAmount || v.monthlyAmount <= 0).length;

  const save = useMutation({
    mutationFn: async () =>
      (await api.put<{ saved: number }>('/payments/plans', {
        items: ozgarganlar.map(([studentId, v]) => ({
          studentId,
          monthlyAmount: v.monthlyAmount,
          discount: v.discount || 0,
          note: v.note.trim() || null,
        })),
      })).data,
    onSuccess: (res) => {
      setTahrirlar({});
      // Qarz ko'rinadigan hamma joy yangi summalar bilan qayta hisoblanadi
      for (const key of ['tuition-plans', 'balances', 'finance', 'cashbook', 'debtors']) qc.invalidateQueries({ queryKey: [key] });
      notifications.show({ color: 'green', message: t('plans.saved', { count: res.saved }) });
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  const filtr = (fn: () => void) => { fn(); setPage(1); };
  const foiz = data && data.activeTotal > 0 ? Math.round((data.withPlan / data.activeTotal) * 100) : 0;

  return (
    <>
      <Card mb="md">
        <Group justify="space-between" mb={6}>
          <Text fw={600}>{t('plans.progress', { done: data?.withPlan ?? 0, total: data?.activeTotal ?? 0 })}</Text>
          <Text size="sm" c="dimmed">{foiz}%</Text>
        </Group>
        <Progress value={foiz} color={foiz === 100 ? 'green' : 'blue'} />
        <Text size="xs" c="dimmed" mt={6}>{t('plans.hint')}</Text>
      </Card>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t('plans.search')}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => filtr(() => setSearch(e.currentTarget.value))}
          w={260}
        />
        <Select
          placeholder={t('plans.allClasses')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classF || null}
          onChange={(v) => filtr(() => setClassF(v ?? ''))}
          clearable
          searchable
          w={200}
        />
        <Switch label={t('plans.onlyMissing')} checked={missing} onChange={(e) => filtr(() => setMissing(e.currentTarget.checked))} />
        <Group ml="auto" gap="sm">
          {ozgarganlar.length > 0 && (
            <Button variant="default" onClick={() => setTahrirlar({})}>{t('plans.discard')}</Button>
          )}
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={ozgarganlar.length === 0 || summasiz > 0}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('plans.save', { count: ozgarganlar.length })}
          </Button>
        </Group>
      </Group>

      {summasiz > 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} mb="md">
          {t('plans.emptyAmount', { count: summasiz })}
        </Alert>
      )}

      <Card p={0}>
        <Table.ScrollContainer minWidth={820}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('plans.student')}</Table.Th>
                <Table.Th>{t('plans.class')}</Table.Th>
                <Table.Th w={190}>{t('plans.monthly')}</Table.Th>
                <Table.Th w={170}>{t('plans.discount')}</Table.Th>
                <Table.Th w={140}>{t('plans.due')}</Table.Th>
                <Table.Th>{t('plans.note')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.items ?? []).map((r) => {
                const v = qiymat(r);
                const ozgargan = !!tahrirlar[r.id];
                const tolanadi = typeof v.monthlyAmount === 'number' ? Math.max(0, v.monthlyAmount - (v.discount || 0)) : null;
                return (
                  <Table.Tr key={r.id} bg={ozgargan ? 'var(--mantine-color-yellow-light)' : undefined}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={500}>{r.fullName}</Text>
                        {r.monthlyAmount == null && !ozgargan && <Badge size="xs" color="gray" variant="light">{t('plans.noAmount')}</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{r.className ?? '—'}</Text></Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        aria-label={t('plans.monthly')}
                        value={v.monthlyAmount}
                        onChange={(x) => ozgartir(r, { monthlyAmount: typeof x === 'number' ? x : '' })}
                        min={0}
                        step={50000}
                        thousandSeparator=" "
                        allowDecimal={false}
                        allowNegative={false}
                        hideControls
                        error={ozgargan && !v.monthlyAmount ? true : undefined}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        aria-label={t('plans.discount')}
                        value={v.discount}
                        onChange={(x) => ozgartir(r, { discount: typeof x === 'number' ? x : 0 })}
                        min={0}
                        step={50000}
                        thousandSeparator=" "
                        allowDecimal={false}
                        allowNegative={false}
                        hideControls
                      />
                    </Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{tolanadi == null ? '—' : formatSom(tolanadi)}</Text></Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        aria-label={t('plans.note')}
                        value={v.note}
                        onChange={(e) => ozgartir(r, { note: e.currentTarget.value })}
                        maxLength={500}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {!isLoading && (data?.items ?? []).length === 0 && (
                <Table.Tr><Table.Td colSpan={6}><Text ta="center" c="dimmed" py="lg">{t('common.noMatch')}</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {(data?.pages ?? 1) > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">{t('common.itemsCount', { count: data?.total ?? 0 })}</Text>
          <Pagination value={page} onChange={setPage} total={data?.pages ?? 1} />
        </Group>
      )}
    </>
  );
}
